"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PopoverBase = void 0;
var tslib_1 = require("tslib");
var React = tslib_1.__importStar(require("react"));
var react_popper_1 = require("react-popper");
var TypeStyle = tslib_1.__importStar(require("typestyle"));
var StatusIcons_1 = require("../../icons/StatusIcons");
var AutoFocus_1 = require("../../utils/AutoFocus");
var Declarative_1 = require("../../utils/Declarative");
var DomUtil_1 = require("../../utils/DomUtil");
var FocusTrap_1 = require("../../utils/FocusTrap");
var GlobalPointerDownOutsideListener_1 = require("../../utils/GlobalPointerDownOutsideListener");
var StackingComponent_1 = require("../../utils/StackingComponent");
var ButtonWidgetShim_1 = require("../Button/ButtonWidgetShim");
var CloseButtonWidget_1 = require("../Button/CloseButtonWidget");
var DropdownButtonStyles_1 = require("../Dropdown/DropdownButtonStyles");
var Styles = tslib_1.__importStar(require("../Popover/PopoverStyles"));
var PopoverStyles_1 = require("../Popover/PopoverStyles");
var TitleBarIconSize = 16;
var NubbinHeight = 8;
var NubbinMargin = 8;
/**
* Shifts the popover away from the trigger element to maintain 8px of space between the 2 elements no matter which side the popever is anchored to.
* {@link https://popper.js.org/docs/v2/modifiers/offset/ popper docs}
* `skidding` shifts the popup parallel to the `placement` edge of the trigger
* `distance` shifts the popup perpendicular to the `placement` edge of the trigger
*/
var PopperOffsetFunction = function (data) {
    var distance = NubbinHeight + NubbinMargin;
    var skidding = 0;
    var dimensionToSkid = data.placement.includes('bottom') || data.placement.includes('top') ? data.reference.width : data.reference.height;
    if (data.placement.includes('start')) {
        skidding = dimensionToSkid / 2 - 20;
    }
    else if (data.placement.includes('end')) {
        skidding = 20 - dimensionToSkid / 2;
    }
    return [skidding, distance];
};
/**
 * Hides the nubbin when it can no longer line up with the trigger.
 * This is mostly relevant for the case of `left` or `right` placement when the titlebar is shown.
 */
var applyNubbinHide = {
    name: 'applyNubbinHide',
    enabled: true,
    phase: 'write',
    fn: function (_a) {
        var _b;
        var state = _a.state;
        var arrow = state.elements.arrow;
        if (arrow) {
            if (((_b = state.modifiersData.arrow) === null || _b === void 0 ? void 0 : _b.centerOffset) !== 0) {
                arrow.classList.add('hidden');
            }
            else {
                arrow.classList.remove('hidden');
            }
        }
    }
};
exports.PopoverBase = function (props) {
    var _a;
    // tslint:disable-next-line:max-line-length deprecation
    var alertType = props.alertType, className = props.className, darkMode = props.darkMode, disableAutoFocus = props.disableAutoFocus, footerButtons = props.footerButtons, getElementToRefocus = props.getElementToRefocus, onClose = props.onClose, onKeyUp = props.onKeyUp, placement = props.placement, popoverContent = props.popoverContent, portalEventTrapOverrides = props.portalEventTrapOverrides, showTitleBar = props.showTitleBar, stackingRootId = props.stackingRootId, style = props.style, testId = props.testId, title = props.title, triggerElement = props.triggerElement, width = props.width, zIndex = props.zIndex, htmlAttrs = tslib_1.__rest(props, ["alertType", "className", "darkMode", "disableAutoFocus", "footerButtons", "getElementToRefocus", "onClose", "onKeyUp", "placement", "popoverContent", "portalEventTrapOverrides", "showTitleBar", "stackingRootId", "style", "testId", "title", "triggerElement", "width", "zIndex"]);
    var _b = React.useState(null), nubbinElement = _b[0], setNubbinElement = _b[1];
    var _c = React.useState(null), popperElement = _c[0], setPopperElement = _c[1];
    var previouslyFocusedElement = React.useRef(null);
    var _d = React.useState(null), stackingRootEl = _d[0], setStackingRootEl = _d[1];
    var titleBarId = React.useState(DomUtil_1.generateId())[0];
    // autofocus something in the popup if it just opened, and refocus the previous element when it closes
    React.useEffect(function () {
        if (!popperElement) {
            return undefined;
        }
        previouslyFocusedElement.current = previouslyFocusedElement.current || document.activeElement;
        AutoFocus_1.autoFocus(disableAutoFocus, popperElement, true);
        return function () { var _a; return (_a = ((getElementToRefocus === null || getElementToRefocus === void 0 ? void 0 : getElementToRefocus()) || previouslyFocusedElement.current)) === null || _a === void 0 ? void 0 : _a.focus(); };
    }, [popperElement]);
    // watch global pointer events to dismiss the popup once it is shown
    React.useEffect(function () {
        if (popperElement && triggerElement) {
            GlobalPointerDownOutsideListener_1.GlobalPointerDownOutsideListener.subscribe(triggerElement, onClose);
        }
        return function () { return GlobalPointerDownOutsideListener_1.GlobalPointerDownOutsideListener.unsubscribe(triggerElement, onClose); };
    }, [popperElement, triggerElement, onClose]);
    var titleBarVisible = showTitleBar || alertType;
    if (!titleBarVisible && typeof title !== 'string') {
        throw new Error('`title` must be a string if there is no visible titlebar');
    }
    /**
     * Custom modifier used to prevent the nubbin from spanning across both the titlebar and the body.
     * Note: needs to be memoized, or else it creates an infinite loop in popper core.
     */
    var arrowModifier = React.useMemo(function () { return ({
        name: 'arrow',
        options: {
            element: nubbinElement,
            padding: function (modifierProps) {
                var topPadding = NubbinMargin;
                if ((modifierProps.placement === 'left' || modifierProps.placement === 'right') && titleBarVisible) {
                    topPadding = 40;
                }
                return {
                    top: topPadding,
                    right: NubbinMargin,
                    bottom: NubbinMargin,
                    left: NubbinMargin,
                };
            }
        }
    }); }, [nubbinElement, titleBarVisible]);
    var _e = react_popper_1.usePopper(triggerElement, popperElement, {
        modifiers: [
            arrowModifier,
            applyNubbinHide,
            {
                name: 'offset',
                options: {
                    offset: PopperOffsetFunction,
                }
            }
        ],
        placement: placement,
    }), styles = _e.styles, attributes = _e.attributes;
    var activePlacement = (_a = attributes.popper) === null || _a === void 0 ? void 0 : _a[PopoverStyles_1.PlacementAttributeName];
    var nubbinIntersectsTitlebar = (activePlacement === null || activePlacement === void 0 ? void 0 : activePlacement.includes('bottom')) || (activePlacement === null || activePlacement === void 0 ? void 0 : activePlacement.includes('left-start')) || (activePlacement === null || activePlacement === void 0 ? void 0 : activePlacement.includes('right-start'));
    var nubbinHasColor = titleBarVisible && nubbinIntersectsTitlebar;
    var handleKeyUp = React.useCallback(function (e) {
        if (e.key === 'Escape') {
            onClose();
            e.preventDefault();
            e.stopPropagation();
        }
        onKeyUp === null || onKeyUp === void 0 ? void 0 : onKeyUp(e);
    }, [onClose, onKeyUp]);
    return (React.createElement(StackingComponent_1.StackingRootContext.Consumer, null, function (stackingRoot) {
        var _a;
        return React.createElement(React.Fragment, null,
            !stackingRootId && !stackingRoot && React.createElement("div", { style: DropdownButtonStyles_1.PopupContainerStyle, ref: setStackingRootEl }),
            React.createElement(StackingComponent_1.StackingRootContext.Provider, { value: stackingRoot || stackingRootEl },
                React.createElement(StackingComponent_1.StackingComponentWrapper, { portalEventTrapOverrides: portalEventTrapOverrides, stackingRootId: stackingRootId, wrapperTestId: Declarative_1.makeTestId(testId, 'PopoverWidget'), zIndex: zIndex },
                    React.createElement("div", tslib_1.__assign({}, (titleBarVisible ? { 'aria-labelledby': titleBarId } : { 'aria-label': title.toString() }), htmlAttrs, { className: TypeStyle.classes(className, Styles.PopoverStyleName, { darkMode: darkMode, titleBarVisible: titleBarVisible }), "data-tb-test-id": Declarative_1.makeTestId(testId, 'PopoverWidget'), onKeyUp: handleKeyUp, onKeyDown: FocusTrap_1.wrapFocusOnTabKeyDown, ref: setPopperElement, role: 'dialog', style: tslib_1.__assign(tslib_1.__assign({ width: width }, styles.popper), style) }, attributes.popper),
                        titleBarVisible && React.createElement("div", { className: TypeStyle.classes(Styles.TitleBarStyleName, alertType), id: titleBarId },
                            React.createElement("div", null,
                                alertType && React.createElement(StatusIcons_1.StatusIcon, { style: { height: TitleBarIconSize, marginRight: 4, width: TitleBarIconSize }, type: alertType }),
                                React.createElement("span", null, title))),
                        React.createElement(CloseButtonWidget_1.CloseButtonWidget, { onClick: onClose, style: Styles.CloseButtonStyle, useDarkStyling: darkMode }),
                        React.createElement("div", tslib_1.__assign({ className: TypeStyle.classes(Styles.NubbinStyleName, { nubbinHasColor: nubbinHasColor }, alertType), ref: setNubbinElement, style: styles.arrow }, (activePlacement && (_a = {}, _a[PopoverStyles_1.PlacementAttributeName] = activePlacement, _a)))),
                        React.createElement("div", { className: TypeStyle.classes(Styles.BodyStyleName, { titleBarVisible: titleBarVisible }) }, popoverContent),
                        footerButtons && React.createElement("div", { className: Styles.FooterStyleName }, footerButtons.map(function (buttonProps, index) {
                            return React.createElement(ButtonWidgetShim_1.ButtonWidget, tslib_1.__assign({ key: index }, buttonProps));
                        }))))));
    }));
};
exports.PopoverBase.displayName = 'PopoverBase';
//# sourceMappingURL=PopoverBase.js.map